import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { IconCopy, IconForward, IconReply, IconSmileOff } from '../components/Icons';
import { REACTION_EMOJIS, REACTION_LABELS } from '../lib/reactions';
import type { MessageItem } from '../store/chat';
import type { MenuAnchor } from './MessageBubble';

const MARGIN = 8;
const GAP = 8; // between the bubble and the menu parts

type Pos = { emoji: { left: number; top: number }; items: { left: number; top: number } };

/**
 * Like Telegram: reactions right above the message, actions right under it, both on the message's side.
 * When that does not fit (a tall message, the screen edge), both go as one stack below or above the
 * message, and as a last resort next to the press point.
 */
function layout(a: MenuAnchor, mine: boolean, e: DOMRect, it: DOMRect): Pos {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const r = a.rect;
  const side = (w: number) => {
    const left = mine ? r.right - w : r.left;
    return Math.min(Math.max(left, MARGIN), Math.max(vw - w - MARGIN, MARGIN));
  };
  const fits = (top: number, h: number) => top >= MARGIN && top + h <= vh - MARGIN;
  const stack = e.height + GAP + it.height;
  let emojiTop: number;
  let itemsTop: number;
  if (fits(r.top - GAP - e.height, e.height) && fits(r.bottom + GAP, it.height)) {
    emojiTop = r.top - GAP - e.height;
    itemsTop = r.bottom + GAP;
  } else {
    let top = r.bottom + GAP;
    if (!fits(top, stack)) top = r.top - GAP - stack;
    if (!fits(top, stack)) top = Math.min(Math.max(a.y - stack / 2, MARGIN), Math.max(vh - stack - MARGIN, MARGIN));
    emojiTop = top;
    itemsTop = top + e.height + GAP;
  }
  return { emoji: { left: side(e.width), top: emojiTop }, items: { left: side(it.width), top: itemsTop } };
}

/**
 * Telegram-style message menu (long press / right click): a row of reactions, and under it the actions.
 */
export function MessageMenu({
  m,
  anchor,
  mine: own,
  myId,
  onToggle,
  onReply,
  onForward,
  onClose,
}: {
  m: MessageItem;
  anchor: MenuAnchor;
  /** The message is the viewer's own (the menu aligns to the right). */
  mine: boolean;
  myId: string | undefined;
  onToggle: (m: MessageItem, emoji: string) => void;
  onReply: (m: MessageItem) => void;
  onForward: (m: MessageItem) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const itemsRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);
  // Only a press that starts on the backdrop closes the menu. The finger that opened it by a long press is
  // still down: its trailing click, and the contextmenu event Android fires a little later for the same
  // long press (by then the backdrop is under the finger), must not close it.
  const armed = useRef(false);
  const mine = m.reactions?.find((r) => r.userId === myId)?.emoji;
  const text: string | undefined = m.payload?.text;

  useLayoutEffect(() => {
    if (!emojiRef.current || !itemsRef.current) return;
    setPos(layout(anchor, own, emojiRef.current.getBoundingClientRect(), itemsRef.current.getBoundingClientRect()));
  }, [anchor, own]);

  useEffect(() => {
    // Keyboard and mouse users get focus in the menu. On touch screens focus stays where it is: moving it
    // out of the text field would close the on-screen keyboard and shift the whole chat.
    if (matchMedia('(pointer: fine)').matches) ref.current?.querySelector<HTMLButtonElement>('button')?.focus({ preventScroll: true });
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pick = (emoji: string) => {
    onToggle(m, emoji);
    onClose();
  };
  const act = (fn: () => void) => () => {
    fn();
    onClose();
  };
  const copy = () => void navigator.clipboard?.writeText(text ?? '').catch(() => {});

  return (
    <>
      <div
        className="menu-backdrop"
        onPointerDown={() => (armed.current = true)}
        onClick={() => armed.current && onClose()}
        onWheel={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          if (armed.current) onClose();
        }}
      />
      <div
        ref={ref}
        className="message-menu"
        role="menu"
        aria-label="Действия с сообщением"
        style={pos ? undefined : { visibility: 'hidden' }}
        // Taps inside the menu must not take focus from the text field (keyboard stays open).
        onPointerDown={(e) => e.pointerType !== 'mouse' && e.preventDefault()}
      >
        <div ref={emojiRef} className="emoji-row" aria-label="Реакции" style={pos?.emoji}>
          {REACTION_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              role="menuitem"
              className={`emoji-btn${mine === emoji ? ' active' : ''}`}
              aria-label={REACTION_LABELS[emoji]}
              title={REACTION_LABELS[emoji]}
              onClick={() => pick(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
        <div ref={itemsRef} className="menu-items" style={pos?.items}>
          <button role="menuitem" className="menu-item" onClick={act(() => onReply(m))}>
            <IconReply size={20} /> Ответить
          </button>
          {text && (
            <button role="menuitem" className="menu-item" onClick={act(copy)}>
              <IconCopy size={20} /> Копировать
            </button>
          )}
          <button role="menuitem" className="menu-item" onClick={act(() => onForward(m))}>
            <IconForward size={20} /> Переслать
          </button>
          {mine && (
            <button role="menuitem" className="menu-item" onClick={() => pick(mine)}>
              <IconSmileOff size={20} /> Убрать реакцию
            </button>
          )}
        </div>
      </div>
    </>
  );
}
